import classnames from 'classnames'
import { useTranslation } from 'next-i18next'
import { ReactNode, useCallback, useEffect, useRef, useState } from 'react'

import { TEST_LOCATORS } from '@src/constants/test-locators'
import ImageDomain from '@src/quince-site-common/src/domains/Image'
import Product from '@src/quince-site-common/src/domains/Product'
import ProductMedia from '@src/quince-site-common/src/domains/ProductMedia'
import ProductVariant from '@src/quince-site-common/src/domains/ProductVariant'
import VideoDomain from '@src/quince-site-common/src/domains/Video'
import useDeviceInfo, { DEVICE_TYPES } from '@src/quince-site-common/src/hooks/useDeviceInfo'
import usePx from '@src/quince-site-common/src/hooks/usePx'
import Heart from '@src/quince-site-common/src/organisms/appLayout/molecules/header/icons/heart.inline.svg'
import productDynamicDataStateManager from '@src/quince-site-common/src/redux/globalStateManager/productDynamicDataStateManager/ProductDynamicDataStateManager'
import getMaxWidthInPx from '@src/quince-site-common/src/utils/getMaxWidthInPx'
import { isImage, isProductImage, isProductVideo, isVideo } from '@src/quince-site-common/src/utils/typeGuards'
import {
  getProductMediaPropertiesForTracking,
  getProductPropertiesForTracking,
} from '@src/telemetry/getProductPropertiesForTracking'
import { TELEMETRY_EVENT_NAMES } from '@src/telemetry/telemetry-events.constants'
import { trackEvent, trackGAEvent, trackInternalEvent } from '@src/telemetry/track'

import ChevronIcon from '../chevronIcon/ChevronIcon'
import Swiper, { NAV_POSITION, SwiperProps } from '../custom-swiper'
import { Image } from '../image'
import { Modal } from '../modal'
import Video, { VIDEO_LOADING_TYPE, VIDEO_PRELOAD_TYPE } from '../video'
import VideoPlaybackIcon from '../video/icons/videoPlayIcon.inline.svg'

import { useStyliticsJumpLink } from './hooks/useStyliticsJumpLink'
import {
  handleImageContainerMouseLeave,
  handleImageContainerMouseMove,
  productGalleryScrollEventTrigger,
} from './SliderImages.helper'
import styles from './sliderImages.module.scss'
import stylesV2 from './sliderImagesV2.module.scss'

export interface SliderImagesProps {
  className?: string
  renderMainImageOverlay?: (index: number) => ReactNode
  media: ProductMedia[]
  hasSelectedOptionImages?: boolean
  leftSliderImage?: string
  leftContainerClassname?: string
  rightContainerClassname?: string
  leftContainerSliderClassname?: string
  hideProductThumbnails?: boolean
  sliderIconClassName?: string
  variants?: ProductVariant[]
  product?: Product
  shouldLoop?: boolean
  pdpHeroImageId?: string
  videoTrackingProperties?: Record<string, unknown>
  renderMediaInfoText?: (index: number) => ReactNode
  afterChangeCallback?: (...args: any[]) => void
  scrollSnap?: boolean
  showNewPdpDesignV2?: boolean
  isWishListed?: boolean
  onWishListToggle?: (e: React.MouseEvent<HTMLElement | SVGElement>) => void
  innerStyle?: React.CSSProperties
  hideWishList?: boolean
  enableStyliticsJumplink?: boolean
  isBundle?: boolean
  showImagePopup: boolean
  setShowImagePopup: (show: boolean) => void
}

const COMMON_SLIDER_SETTINGS: SwiperProps = {
  // @i18n-ignore
  id: 'slider-images-common-slider',
  children: [],
  showDots: false,
  infinite: { loop: true, loopedSlides: 1 },
  isVertical: true,
  showNavigator: false,
  numberOfVisibleSlides: 5,
}

const MOBILE_SLIDER_SETTINGS: SwiperProps = {
  ...COMMON_SLIDER_SETTINGS,
  numberOfVisibleSlides: 1,
  isVertical: false,
  infinite: { loop: true, loopedSlides: 1 },
}

const DESKTOP_SLIDER_SETTINGS: SwiperProps = {
  ...COMMON_SLIDER_SETTINGS,
  virtualOptions: {
    enabled: true,
  },
  // TODO: Change this to true when slider is fixed for image thumbnails on desktop
  showNavigator: false,
  isChevronIcon: true,
  navPosition: NAV_POSITION.VERTICAL,
}

const getSliderSettings = (media: (ImageDomain | VideoDomain)[], isMobile: boolean) => {
  const mobileSliderSettings =
    media.length === 1
      ? MOBILE_SLIDER_SETTINGS
      : {
          ...MOBILE_SLIDER_SETTINGS,
          variableWidth: true,
        }
  return isMobile ? mobileSliderSettings : DESKTOP_SLIDER_SETTINGS
}

const SliderImages = ({
  className,
  media = [],
  renderMainImageOverlay,
  hasSelectedOptionImages,
  leftSliderImage,
  leftContainerClassname,
  leftContainerSliderClassname,
  hideProductThumbnails,
  sliderIconClassName,
  rightContainerClassname,
  variants,
  product,
  shouldLoop = true,
  pdpHeroImageId = '',
  videoTrackingProperties,
  renderMediaInfoText,
  afterChangeCallback,
  scrollSnap,
  showNewPdpDesignV2,
  isWishListed,
  onWishListToggle,
  innerStyle,
  hideWishList,
  enableStyliticsJumplink,
  showImagePopup,
  setShowImagePopup,
}: SliderImagesProps) => {
  const deviceInfo = useDeviceInfo()
  const isMobile = deviceInfo.deviceType === DEVICE_TYPES.MOBILE
  const [index, setIndex] = useState(0)

  const enableHighQualityImageDownloadOnHover = true
  const [isHoverContainerEnabled, setIsHoverContainerEnabled] = useState(false)
  const lastViewedImageIndexRef = useRef(0)
  const finalStyles = showNewPdpDesignV2 ? stylesV2 : styles
  const videoIndex = media.findIndex(md => isProductVideo(md))

  const classes = classnames(finalStyles.container, className, {
    [finalStyles.hideThumbnailsContainer as string]: hideProductThumbnails,
  })
  const { t } = useTranslation('common')
  // Resetting the index in case the image changes.
  const id = media.reduce((acc, md) => {
    let val = ''
    if (isProductImage(md)) {
      val = md.image.url
    } else if (isProductVideo(md)) {
      val = md.video.url
    }
    return acc + val
  }, '')

  useEffect(() => {
    setIndex(0)
  }, [id])

  useEffect(() => {
    setIsHoverContainerEnabled(true)
  }, [])

  const settings = getSliderSettings(
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    media.map(i => (isProductVideo(i) ? i.video : i.image)).filter(pi => !!pi) as ImageDomain[],
    isMobile,
  )

  const setActiveImage = (imgIdx: number) => {
    setIndex(imgIdx)
  }
  const handleImageClick = (index: number) => {
    setActiveImage(index)
    setShowImagePopup(true)
  }

  const closeImagePopup = () => {
    setActiveImage(lastViewedImageIndexRef.current)
    setShowImagePopup(false)
  }

  const imageContainerRef = useRef<HTMLDivElement>(null)
  const hoverContainerRef = useRef<HTMLDivElement>(null)
  const sideBoxParentContainerRef = useRef<HTMLDivElement>(null)
  const sideBoxImageContainerRef = useRef<HTMLDivElement>(null)

  const handleImageContainerMouseEnter = (selectedMedia: ProductMedia) => {
    trackInternalEvent(TELEMETRY_EVENT_NAMES.ZOOM_PRODUCT_IMAGE, {
      context: {
        productProperties: product
          ? {
              ...getProductPropertiesForTracking(product, variants?.[0]?.svid?.toString()),
              ...getProductMediaPropertiesForTracking(media, selectedMedia),
            }
          : {},
      },
    })
    trackGAEvent(TELEMETRY_EVENT_NAMES.ZOOM_PRODUCT_IMAGE, {
      context: {
        productProperties: product
          ? {
              ...getProductPropertiesForTracking(product, variants?.[0]?.svid?.toString()),
              ...getProductMediaPropertiesForTracking(media, selectedMedia),
            }
          : {},
      },
    })
  }

  const getPxFromRem: (withRem: number, withUnits: boolean) => string | number = usePx()

  const selectedProductMedia = media[index]
  const mainMedia =
    selectedProductMedia &&
    (isProductImage(selectedProductMedia) ? selectedProductMedia.image : selectedProductMedia.video)

  const iconClasses = classnames({
    [`${sliderIconClassName}`]: true,
    ['slider-images-icon-wrapper']: true,
  })
  const handleOnScrollEvent = useCallback(
    () => productGalleryScrollEventTrigger(product, variants?.[0]?.svid?.toString()),
    [product, variants],
  )

  const [hasVideoPlayedOnce, setHasVideoPlayedOnce] = useState(false)
  const { initStylitics, onJumplinkClick, colorwayId } = useStyliticsJumpLink(
    product,
    selectedProductMedia,
    !!enableStyliticsJumplink,
  )
  const productHasStyliticsData = productDynamicDataStateManager.getProductHasStyliticsData(colorwayId)
  useEffect(() => {
    if (!enableStyliticsJumplink) return
    let isMounted = true
    initStylitics(isMounted)
    return () => {
      isMounted = false
    }
  }, [initStylitics])

  return (
    <>
      <div className={classes}>
        {hideProductThumbnails ? null : (
          <div
            data-testid={`swiper-left-container`}
            className={classnames(
              finalStyles.container__left,
              media.length === 1 && finalStyles.container__left_single,
              leftContainerClassname,
            )}
          >
            <Swiper
              key={id}
              className={classnames(
                finalStyles.container__left__carousel,
                finalStyles.container__left__slider,
                media.length === 1 && finalStyles.container__left__slider_single,
                leftContainerSliderClassname,
              )}
              prevIconClass={iconClasses}
              nextIconClass={iconClasses}
              {...settings}
              id={id}
              slideClassName={finalStyles.container__left__slider__modal__sliderWrapper}
              infinite={{ loop: isMobile && shouldLoop, loopedSlides: 1 }}
              handleOnScrollEvent={handleOnScrollEvent}
              afterChange={swiperInstance => {
                if (deviceInfo.deviceType !== DEVICE_TYPES.DESKTOP) {
                  lastViewedImageIndexRef.current = swiperInstance.activeIndex
                  setActiveImage(swiperInstance.activeIndex)
                }
                afterChangeCallback?.(swiperInstance.activeIndex, swiperInstance.swipeDirection)
              }}
              scrollSnap={scrollSnap}
            >
              {media.map((med, index) => {
                return (
                  <div
                    className={classnames(
                      finalStyles.container__left__slider__image,
                      media.length === 1 && finalStyles.container__left__slider__singleImage,
                      leftSliderImage,
                    )}
                    key={isProductImage(med) ? med.image.url : med.video.url}
                    onClick={
                      isMobile
                        ? () => {
                            handleImageClick(index)
                          }
                        : undefined
                    }
                    onMouseEnter={() => {
                      setActiveImage(index)
                      trackEvent(TELEMETRY_EVENT_NAMES.PRODUCT_GALLERY_VIEWED, {
                        product_id: variants?.[0]?.productId,
                        context: {
                          productProperties: product
                            ? {
                                ...getProductPropertiesForTracking(product, variants?.[0]?.svid?.toString()),
                                ...getProductMediaPropertiesForTracking(media, med),
                              }
                            : {},
                        },
                      })
                    }}
                  >
                    {/* The following check only renders the first image in mobile view, any not the others to reduce the SSR markup. */}
                    {/* eslint-disable-next-line @typescript-eslint/no-unnecessary-condition */}
                    {((isMobile && index === 0) || isHoverContainerEnabled) && isProductImage(med) && med.image && (
                      <>
                        <Image
                          alt={med.image.alt ?? med.image.title}
                          {...ImageDomain.makeImageProps(med.image)}
                          // @i18n-ignore
                          loading={isMobile ? 'eager' : 'lazy'}
                          hasDynamicImage
                          maxWidth={getMaxWidthInPx({
                            maxWidthDesktopInRem: finalStyles.sliderImageDesktopWidth as string,
                            maxWidthMobileInRem: finalStyles.sliderImageMobileWidth as string,
                            deviceType: deviceInfo.deviceType,
                          })}
                          // @i18n-ignore
                          fetchPriority={isMobile && index === 0 ? 'high' : 'auto'}
                        />
                        {renderMainImageOverlay && isMobile && index === 0 ? renderMainImageOverlay(index) : null}
                      </>
                    )}
                    {/* eslint-disable-next-line @typescript-eslint/no-unnecessary-condition */}
                    {isProductVideo(med) && med.video && (
                      <div className={finalStyles.container__left__slider__image__videoPoster}>
                        <Image
                          alt={med.videoPoster?.title}
                          {...ImageDomain.makeImageProps(med.videoPoster)}
                          // @i18n-ignore
                          loading={'eager'}
                          hasDynamicImage
                          maxWidth={getMaxWidthInPx({
                            maxWidthDesktopInRem: finalStyles.sliderImageDesktopWidth as string,
                            maxWidthMobileInRem: finalStyles.sliderImageMobileWidth as string,
                            deviceType: deviceInfo.deviceType,
                          })}
                        />
                        <div className={finalStyles.container__left__slider__image__videoPoster__playbackIconContainer}>
                          <VideoPlaybackIcon />
                        </div>
                      </div>
                    )}
                  </div>
                )
              })}
            </Swiper>
          </div>
        )}
        <div
          data-testid={'swiper-right-container'}
          className={classnames(finalStyles.container__right, rightContainerClassname)}
        >
          {/* eslint-disable-next-line i18next/no-literal-string */}
          {mainMedia && !mainMedia.url.includes('video') && isImage(mainMedia) && (
            <div
              ref={imageContainerRef}
              className={finalStyles.container__right__imageContainer}
              onMouseEnter={() => {
                handleImageContainerMouseEnter(selectedProductMedia)
              }}
              onMouseMove={e => {
                handleImageContainerMouseMove({
                  eventData: { clientX: e.clientX, clientY: e.clientY },
                  getPxFromRem,
                  imageContainerRef,
                  hoverContainerRef,
                  sideBoxImageContainerRef,
                  sideBoxParentContainerRef,
                })
              }}
              onMouseLeave={() => {
                handleImageContainerMouseLeave({
                  hoverContainerRef,
                  sideBoxImageContainerRef,
                  sideBoxParentContainerRef,
                })
              }}
            >
              <Image
                alt={mainMedia.alt ?? mainMedia.title}
                {...ImageDomain.makeImageProps(mainMedia)}
                {...(pdpHeroImageId ? { id: pdpHeroImageId } : null)}
                // @i18n-ignore
                loading={'eager'}
                // @i18n-ignore
                fetchPriority={!isMobile && index === 0 ? 'high' : 'auto'}
                hasDynamicImage
              />
              {renderMainImageOverlay ? renderMainImageOverlay(index) : null}
              {isHoverContainerEnabled && hideProductThumbnails ? null : (
                <div className={finalStyles.container__right__hoverContainer} ref={hoverContainerRef} />
              )}
              {enableStyliticsJumplink && productHasStyliticsData && !isMobile && (
                <div className={finalStyles.stylitics_jumplink_container}>
                  <div onClick={onJumplinkClick} id="stylitics-jumplink" className={finalStyles.stylitics_jumplink}>
                    {t('shop_the_look')}
                  </div>
                </div>
              )}
            </div>
          )}
          {/* eslint-disable-next-line i18next/no-literal-string */}
          {mainMedia?.url.includes('video') && isVideo(mainMedia) && (
            <Video
              key={mainMedia.url}
              {...VideoDomain.makeVideoProps(mainMedia)}
              loop
              muted
              controls
              preload={VIDEO_PRELOAD_TYPE.Metadata}
              className={finalStyles.container__right__video}
              posterImage={
                selectedProductMedia && isProductVideo(selectedProductMedia)
                  ? selectedProductMedia.videoPoster
                  : undefined
              }
              autoplay
              tracker={`pdp-image-carousel__${videoIndex}`}
              trackingProperties={videoTrackingProperties}
              shouldTrackPlay={isMobile ? false : true}
              hasPlayedOnce={hasVideoPlayedOnce}
              onMarkPlayed={() => {
                setHasVideoPlayedOnce(true)
              }}
            />
          )}
          {hideProductThumbnails ? (
            <>
              <button
                className={finalStyles.chevronIconBtn}
                onClick={() => {
                  setActiveImage(index - 1)
                }}
                disabled={index === 0}
              >
                <ChevronIcon className={classnames(finalStyles.chevronIcon, finalStyles.chevronIconLeft)} />
              </button>
              <button
                className={finalStyles.chevronIconBtn}
                onClick={() => {
                  setActiveImage(index + 1)
                }}
                disabled={index === media.length - 1}
              >
                <ChevronIcon className={classnames(finalStyles.chevronIcon, finalStyles.chevronIconRight)} />
              </button>
            </>
          ) : null}
        </div>
        {showNewPdpDesignV2 && !hideWishList && (
          <div
            className={classnames(
              finalStyles.container__wishlistIconContainer,
              isWishListed && finalStyles.container__wishlistIconContainer_wishlisted,
            )}
          >
            <Heart
              data-testid={
                isWishListed ? TEST_LOCATORS.REMOVE_FROM_WISHLIST_BUTTON : TEST_LOCATORS.ADD_TO_WISHLIST_BUTTON
              }
              style={innerStyle}
              className={finalStyles.container__header__icon}
              onClick={onWishListToggle}
            />
          </div>
        )}
        {enableStyliticsJumplink && productHasStyliticsData && isMobile && (
          <div onClick={onJumplinkClick} id="stylitics-jumplink" className={finalStyles.stylitics_jumplink}>
            {t('shop_the_look')}
          </div>
        )}
        {/**
         * Don't render side box container on mobile to avoid unnecessary image download
         */}
        {!isMobile &&
          isHoverContainerEnabled &&
          !hideProductThumbnails &&
          mainMedia &&
          // eslint-disable-next-line i18next/no-literal-string
          !mainMedia.url.includes('video') &&
          isImage(mainMedia) && (
            <div className={classnames(finalStyles.container__sideBoxParentContainer)} ref={sideBoxParentContainerRef}>
              <div
                ref={sideBoxImageContainerRef}
                className={finalStyles.container__sideBoxParentContainer__sizeBoxImageContainer}
              >
                <Image
                  alt={mainMedia.alt ?? mainMedia.title}
                  {...ImageDomain.makeImageProps(mainMedia)}
                  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
                  // @i18n-ignore
                  loading={hasSelectedOptionImages && enableHighQualityImageDownloadOnHover ? 'eager' : 'lazy'}
                  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
                  quality={hasSelectedOptionImages && enableHighQualityImageDownloadOnHover ? 90 : 50}
                  hasDynamicImage
                />
              </div>
            </div>
          )}
      </div>

      {isMobile && renderMediaInfoText?.(index)}

      <Modal
        open={showImagePopup}
        onClose={closeImagePopup}
        className={finalStyles.mobileImagePopupModal}
        modalWrapperClassName={finalStyles.mobileImagePopupModal_module_container}
        closeButtonClassName={finalStyles.mobileImagePopupModal_close_button}
        showCloseButton
        closeButtonContainerClassName={finalStyles.mobileImagePopupModal_close_button_container}
      >
        <Swiper
          key={id}
          className={finalStyles.container__left__slider__modal}
          {...settings}
          numberOfVisibleSlides={1}
          afterChange={swiperInstance => {
            setActiveImage(swiperInstance.activeIndex)
          }}
          variableWidth
          infinite={{ loop: true, loopedSlides: 1 }}
          slideClassName={finalStyles.container__left__slider__modal__sliderWrapper}
          initialSlide={index}
          // For modal we dont need horizontal caching.
          shouldCacheScrollPosition={false}
          scrollSnap={scrollSnap}
          showDots={isMobile && showNewPdpDesignV2}
          paginationClassName={finalStyles.container__left__slider__modal__pagination}
          paginationDotClassName={finalStyles.container__left__slider__modal__paginationDot}
          paginationDotActiveClassName={finalStyles.container__left__slider__modal__paginationDotActive}
        >
          {media.map((med, index) => {
            const selectedProductMedia = media[index]
            const mainMedia =
              selectedProductMedia &&
              (isProductImage(selectedProductMedia) ? selectedProductMedia.image : selectedProductMedia.video)

            return (
              <div
                className={classnames(
                  finalStyles.container__left__slider__modal__image,
                  media.length === 1 && finalStyles.container__left__slider__singleImage,
                )}
                key={mainMedia?.url}
              >
                {/* eslint-disable-next-line @typescript-eslint/no-unnecessary-condition */}
                {isProductImage(med) && med.image && (
                  <Image
                    alt={med.image.alt ?? med.image.title}
                    {...ImageDomain.makeImageProps(med.image)}
                    // @i18n-ignore
                    loading={index === 0 ? 'eager' : undefined}
                    hasDynamicImage
                    maxWidth={getMaxWidthInPx({
                      maxWidthDesktopInRem: finalStyles.modalImageWidthMobile as string,
                      maxWidthMobileInRem: finalStyles.modalImageWidthMobile as string,
                      deviceType: deviceInfo.deviceType,
                    })}
                  />
                )}
                {/* eslint-disable-next-line @typescript-eslint/no-unnecessary-condition */}
                {isProductVideo(med) && med.video && (
                  <Video
                    {...VideoDomain.makeVideoProps(med.video)}
                    loop
                    muted
                    controls
                    preload={VIDEO_PRELOAD_TYPE.Auto}
                    loadingType={VIDEO_LOADING_TYPE.Eager}
                    className={finalStyles.container__right__video__modal}
                    wrapperClassName={finalStyles.popup_video_item_wrapper}
                    posterImage={
                      selectedProductMedia && isProductVideo(selectedProductMedia)
                        ? selectedProductMedia.videoPoster
                        : undefined
                    }
                    autoplay
                    tracker={`pdp-image-carousel__${videoIndex}`}
                    trackingProperties={videoTrackingProperties}
                    shouldTrackPlay
                    hasPlayedOnce={hasVideoPlayedOnce}
                    onMarkPlayed={() => {
                      setHasVideoPlayedOnce(true)
                    }}
                  />
                )}
              </div>
            )
          })}
        </Swiper>
      </Modal>
    </>
  )
}

export default SliderImages
